import { getExtensionValue } from "@medplum/core";
import { ChargeItem, Extension, Identifier, Invoice } from "@medplum/fhirtypes";

import { v4 as uuidv4 } from "uuid";

import { CombinedProcedureCodesValueSet, PaymentMethod } from "@/constants/billing";
import { Extensions } from "@/fhir/enums/extensions";
import {
  QuiltedHealthCodeSystems,
  QuiltedHealthIdentifierSystems,
} from "@/fhir/enums/quilted-health";
import { getExtensionValues } from "@/utils/medplum";

export function getCombinedProcedureCodesValueSetUrl(practiceId: string): string {
  return `${CombinedProcedureCodesValueSet.BASE_URL}/${practiceId}`;
}

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return "-";
  return currencyFormatter.format(value);
}
/**
 * A statement family's identity, copied by every artifact the statement produces (Claim,
 * ClaimResponse, PaymentReconciliation) so one indexed `identifier` query resolves them to each
 * other. `value` comes from `newBillingIdentifierValue`.
 */
export const billingIdentifier = (value: string): Identifier => ({
  system: QuiltedHealthIdentifierSystems.BillingIdentifier,
  value,
});

/**
 * A fresh billing-identifier value: a bare v4 UUID — random, unique, and meaningless on purpose.
 *
 * A statement bundles whatever charges the biller selected, which may span several Encounters or
 * none at all, so there is no resource whose reference could stand in as the identity — it is minted
 * instead. Random rather than sequential, so it needs nothing from the server to be unique.
 *
 * Mint once per statement *family*: a sub-statement re-bills the same charges under the next payer
 * and must copy its parent Invoice's value, not mint its own, or the family reads as empty and every
 * sub-statement after the first keeps proposing `-1` until allocation gives up.
 */
export const newBillingIdentifierValue = (): string => uuidv4();

/** The statement number, chronological per practice. */
export const invoiceIdentifier = (statementNumber: string): Identifier => ({
  system: QuiltedHealthIdentifierSystems.InvoiceIdentifier,
  value: statementNumber,
});

/**
 * The single shape the payment-method extension takes on both the Service's `ChargeItem`s and the
 * Invoice derived from them. Shared so the statement copies it verbatim rather than re-deriving the
 * value type — reader and writer cannot drift apart while they both go through here.
 */
export const paymentMethodExtension = (method: PaymentMethod): Extension => ({
  url: Extensions.ServicePayer,
  valueString: method,
});

/**
 * The tier a statement is billed under, as `Coverage.order`.
 *
 * `Invoice.recipient` names the payer Organization, and a client can hold a Primary and a Secondary
 * plan through the same insurer, so the payer alone does not say which coverage was billed. Written
 * here at generation and read back by `getInvoiceBilledCoverageOrder`, which is the reader that
 * resolves the same extension on the statement detail page.
 *
 * @param order The billed `Coverage.order`.
 * @returns The extension to place on the Invoice.
 */
export const billedCoverageOrderExtension = (order: number): Extension => ({
  url: Extensions.BilledCoverageOrder,
  valueInteger: order,
});

/**
 * Whether a charge bills a facility fee — the overhead a facility charges for the space, equipment
 * and staff behind a visit, billed separately from the professional work.
 *
 * Matched on the coding *system*, not the code. The facility fee CodeSystem is a category that ships
 * with one revenue code (`0724`) and is expected to grow, and a bare `0724` is not unique across
 * systems. Every coding is checked rather than just the first: the pickers write one, but a charge
 * imported or edited elsewhere may carry a translation alongside it.
 */
export function isFacilityFeeChargeItem(chargeItem: ChargeItem): boolean {
  return (
    chargeItem.code?.coding?.some(
      (coding) => coding.system === QuiltedHealthCodeSystems.FacilityFee,
    ) ?? false
  );
}

/**
 * The has-facility-fee marker, for the charges a statement is being generated from — or nothing when
 * none of them is a facility fee.
 *
 * Spread into `Invoice.extension` at generation time and never removed afterwards. It records a fact
 * about what was billed, so it stays true however the statement is edited later; whether the
 * statement is *blocked* is derived from it at read time by `isMissingFacilityAdmission`, which is
 * what lets a cleared facility admission re-block the statement.
 *
 * Absent rather than `valueBoolean: false` for a statement with no facility fee: presence is the
 * signal, so there is one shape to read rather than two.
 */
export function facilityFeeExtensions(chargeItems: ChargeItem[]): Extension[] {
  if (!chargeItems.some(isFacilityFeeChargeItem)) return [];

  return [{ url: Extensions.HasFacilityFee, valueBoolean: true }];
}

/**
 * Whether the statement bills a facility fee, as recorded when it was generated.
 *
 * The read side of {@link facilityFeeExtensions}, and here beside it so the marker's shape — the url
 * and `valueBoolean: true` being the signal — lives in one file rather than in two modules that have
 * to agree.
 *
 * An immutable fact: statement generation stamps it and nothing removes it, so it stays true after
 * the facility admission has been filled in — which is what makes `isMissingFacilityAdmission`
 * re-block a statement whose admission is later cleared, and what decides the statement shows a
 * Facility Admission block at all.
 *
 * Statements generated before this marker shipped carry nothing and read as `false`. That is
 * deliberate: no backfill was run, so the facility-admission requirement applies going forward
 * only.
 */
export function hasFacilityFee(invoice: Invoice): boolean {
  return (
    invoice.extension?.some(
      (extension) => extension.url === Extensions.HasFacilityFee && extension.valueBoolean === true,
    ) ?? false
  );
}

/**
 * Returns the CPT or custom modifiers saved on a `ChargeItem` itself, in the order they were
 * written.
 *
 * The extension repeats, one entry per modifier. Values are trimmed and blank ones are dropped, so
 * a modifier saved with stray whitespace prints the same wherever it is read.
 *
 * Two kinds of charge carry modifiers of their own: a custom service, which has no Procedure behind
 * it, and an encounter charge a biller has edited from the billing tab. Every other charge keeps
 * its modifiers on the Procedure it was captured from — see {@link Extensions.ProcedureModifiers}.
 */
export const getChargeItemProcedureModifiers = (chargeItem: ChargeItem): string[] =>
  ((getExtensionValues(chargeItem, Extensions.ChargeItemProcedureModifiers) ?? []) as string[])
    .map((modifier) => modifier.trim())
    .filter(Boolean);

/**
 * The single shape the biller-edited marker takes, so the write and the reads below cannot drift
 * apart on the value type.
 *
 * Written on every edit a biller saves from the billing tab, whatever modifiers the edit ended
 * with. See {@link hasBillerEditedChargeItemModifiers} for what reads it.
 */
export const billerEditedModifiersExtension = (): Extension => ({
  url: Extensions.ChargeItemModifiersEdited,
  valueBoolean: true,
});

/**
 * Returns whether the modifiers on a charge are the ones a biller saved.
 *
 * A charge nobody has edited carries no modifiers of its own and reads them off the Procedure it
 * was captured from. A biller who removes every modifier leaves the same empty list behind, so the
 * marker is what separates the two. Without it a cleared charge would be read as unedited and the
 * provider's modifiers would come back onto the row.
 */
export const hasBillerEditedChargeItemModifiers = (chargeItem: ChargeItem): boolean =>
  getExtensionValue(chargeItem, Extensions.ChargeItemModifiersEdited) === true;
